import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Overline, Reveal } from "./Reveal";
import Modal from "./Modal";
import { apiFetch, sendApplicationEmail, IS_STATIC } from "@/lib/api";
import { getContent, loadRemoteContent, hasLocalOverrides } from "@/lib/staticData";

const PERKS = [
  "Above-award rates, paid weekly",
  "Certificate III support for apprentices",
  "Premium tools & PPE supplied",
  "Four-day weeks available on commercial crews",
];

const EMPTY_FORM = { name: "", email: "", phone: "", role: "", portfolio: "", message: "" };

export default function CareersSection() {
  const [roles, setRoles] = useState(IS_STATIC ? getContent().roles : []);
  const [applyRole, setApplyRole] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (IS_STATIC) {
      loadRemoteContent().then((c) => {
        if (c && !hasLocalOverrides()) setRoles(c.roles);
      });
      return;
    }
    apiFetch("/roles").then(setRoles).catch(() => {});
  }, []);

  const openApply = (roleTitle) => {
    setForm({ ...EMPTY_FORM, role: roleTitle });
    setApplyRole(roleTitle);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (!IS_STATIC) {
        await apiFetch("/applications", { method: "POST", body: form });
      }
      sendApplicationEmail(form);
      toast.success("Application received — we'll be in touch within two business days.");
      setApplyRole(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err.message || "Could not send application. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="careers" className="border-y border-border bg-card/50 py-24 sm:py-32" data-testid="careers-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <Overline>Careers at Strovyn</Overline>
          <h2 className="mt-4 font-serif text-4xl font-bold tracking-tight sm:text-5xl">
            BUILD THE STANDARD<br />WITH US<span className="text-accent">.</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            We hire craftspeople who care about the last millimetre. Join a crew
            that treats Adelaide's homes and heritage like gallery work.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {roles.length === 0 ? (
              <p className="border border-border p-8 text-muted-foreground" data-testid="careers-no-roles">
                No open roles right now — but we're always keen to meet good people.
                Send an expression of interest below.
              </p>
            ) : (
              <div className="space-y-4">
                {roles.map((r, i) => (
                  <Reveal key={r.id} delay={i * 0.06}>
                    <div className="group flex flex-col gap-4 border border-border bg-background p-6 transition-colors hover:border-accent sm:flex-row sm:items-center sm:p-7">
                      <div className="flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                          {r.type}
                        </p>
                        <h3 className="mt-2 font-serif text-xl font-semibold tracking-tight sm:text-2xl">
                          {r.title}
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground">{r.summary}</p>
                        <p className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          <MapPin className="h-3 w-3" /> {r.location}
                        </p>
                      </div>
                      <button
                        data-testid={`careers-role-apply-button-${i}`}
                        onClick={() => openApply(r.title)}
                        className="flex shrink-0 items-center gap-2 border border-foreground/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors group-hover:border-accent group-hover:bg-accent group-hover:text-accent-foreground"
                      >
                        Apply <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}

            <button
              data-testid="careers-interest-button"
              onClick={() => openApply("Expression of Interest")}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-accent underline-offset-4 hover:underline"
            >
              Don't see your role? Send an expression of interest
            </button>
          </div>

          <Reveal className="lg:col-span-5">
            <div className="border border-border bg-background p-8">
              <h3 className="font-serif text-2xl font-semibold tracking-tight">Why Strovyn?</h3>
              <ul className="mt-6 space-y-4">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    {p}
                  </li>
                ))}
              </ul>
              <p className="mt-8 border-t border-border pt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-muted-foreground">
                All roles based in Adelaide, SA. Valid SA driver's licence
                required. White Card provided for the right candidate.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <Modal open={!!applyRole} onClose={() => setApplyRole(null)} label="Apply for role">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Application</p>
        <h3 className="mt-2 font-serif text-2xl font-bold tracking-tight">{applyRole}</h3>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="careers-application-form">
          <input required placeholder="Full name" value={form.name} onChange={set("name")}
            data-testid="application-name-input"
            className="w-full border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent" />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")}
            data-testid="application-email-input"
            className="w-full border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent" />
          <input placeholder="Phone" value={form.phone} onChange={set("phone")}
            data-testid="application-phone-input"
            className="w-full border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent" />
          <input placeholder="Portfolio or LinkedIn (optional)" value={form.portfolio} onChange={set("portfolio")}
            data-testid="application-portfolio-input"
            className="w-full border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent" />
          <textarea placeholder="Tell us about your experience" rows={4} value={form.message} onChange={set("message")}
            data-testid="application-message-input"
            className="w-full resize-none border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent" />
          <button type="submit" disabled={sending}
            data-testid="application-submit-button"
            className="w-full bg-accent px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground transition-opacity disabled:opacity-50">
            {sending ? "Sending..." : "Submit Application"}
          </button>
        </form>
      </Modal>
    </section>
  );
}
